import { useContext, useState, useEffect } from 'react';
import { ArrowLeft, Edit, Calendar, Target, Users, MapPin, AlertTriangle, FileText, Building2, User, Clock, Play, Eye, XCircle, Award, ChevronDown, ChevronRight, List, RefreshCw, Download, HelpCircle, Lightbulb, TrendingUp, Pause } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import AuthContext from '../../../../../../context/AuthContext';
import { useToast } from '../../../../../../context/ToastContext';
import auditPlanService from '../../../../../../services/audit_services/auditPlanService';
import auditSectionService from '../../../../../../services/audit_services/auditSectionService';
import auditQuestionService from '../../../../../../services/audit_services/auditQuestionService';
import recommendationService from '../../../../../../services/audit_services/recommendationService';
import exportAuditPlanService from '../../../../../../services/audit_services/export_pdf_services/exportAuditPlanService';
import exportQuestionService from '../../../../../../services/audit_services/export_pdf_services/exportQuestionService';
import attachmentService from '../../../../../../services/audit_services/attachmentService';
import followupAttachmentService from '../../../../../../services/audit_services/followupAttachmentService';

// Import modals
import ReleaseModal from './auditPlanModals/ReleaseModal';
import CloseModal from './auditPlanModals/CloseModal';
import AchieveModal from './auditPlanModals/AchieveModal';
import UnachieveModal from './auditPlanModals/UnachieveModal';
import ReopenModal from './auditPlanModals/ReopenModal';
import PublishModal from './auditPlanModals/PublishModal';
import PausePublicationModal from './auditPlanModals/PausePublicationModal';

export default function AuditPlanDetail() {
  const { id } = useParams();
  const { authTokens, hasRole } = useContext(AuthContext);
  const { showError, showSuccess } = useToast();

  const [auditPlan, setAuditPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Sections state
  const [showSections, setShowSections] = useState(false);
  const [sections, setSections] = useState([]);
  const [sectionsLoading, setSectionsLoading] = useState(false);

  // Questions state - keyed by section ID
  const [sectionQuestions, setSectionQuestions] = useState({});
  const [questionsLoading, setQuestionsLoading] = useState({});
  const [showQuestions, setShowQuestions] = useState({});

  // Recommendations state - keyed by question ID
  const [questionRecommendations, setQuestionRecommendations] = useState({});
  const [recommendationsLoading, setRecommendationsLoading] = useState({});
  const [showRecommendations, setShowRecommendations] = useState({});

  // Follow-ups state - keyed by recommendation ID
  const [recommendationFollowUps, setRecommendationFollowUps] = useState({});
  const [followUpsLoading, setFollowUpsLoading] = useState({});
  const [showFollowUps, setShowFollowUps] = useState({});
  
  // Export states
  const [exportingQuestions, setExportingQuestions] = useState({});
  
  // Download states
  const [downloadingAttachments, setDownloadingAttachments] = useState({});

  // Modal states
  const [showReleaseModal, setShowReleaseModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showAchieveModal, setShowAchieveModal] = useState(false);
  const [showUnachieveModal, setShowUnachieveModal] = useState(false);
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showPausePublicationModal, setShowPausePublicationModal] = useState(false);

  useEffect(() => {
    fetchAuditPlan();
  }, [id]);

  const fetchAuditPlan = async () => {
    setLoading(true);
    
    try {
      const result = await auditPlanService.getAuditPlanDetail(authTokens.access, id);
      
      if (result.success) {
        setAuditPlan(result.data);
      } else {
        console.error('Error fetching audit plan:', result.error);
        showError(result.error || 'Error loading audit plan details');
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching audit plan:', error);
      showError('Error loading audit plan details');
      setLoading(false);
    }
  };

  const fetchSections = async () => {
    if (showSections) {
      setShowSections(false);
      return;
    }

    setSectionsLoading(true);
    setShowSections(true);
    
    try {
      const result = await auditSectionService.getAuditSections(
        authTokens.access,
        id ,
        { page: 1, page_size: 100 }
      );
      
      if (result.success) {
        setSections(result.data.results || []);
      } else {
        showError(result.error || 'Error loading sections');
      }
      setSectionsLoading(false);
    } catch (error) {
      console.error('Error fetching sections:', error);
      showError('Error loading sections');
      setSectionsLoading(false);
    }
  };

  const refreshSections = async () => {
    setSectionsLoading(true);
    
    try {
      const result = await auditSectionService.getAuditSections(
        authTokens.access,
         id,
        { page: 1, page_size: 100 }
      );
      
      if (result.success) {
        setSections(result.data.results || []);
        showSuccess('Sections refreshed successfully');
      } else {
        showError(result.error || 'Error refreshing sections');
      }
      setSectionsLoading(false);
    } catch (error) {
      console.error('Error refreshing sections:', error);
      showError('Error refreshing sections');
      setSectionsLoading(false);
    }
  };

  const fetchQuestions = async (sectionId) => {
    if (showQuestions[sectionId]) {
      setShowQuestions(prev => ({ ...prev, [sectionId]: false }));
      return;
    }

    setQuestionsLoading(prev => ({ ...prev, [sectionId]: true }));
    setShowQuestions(prev => ({ ...prev, [sectionId]: true }));
    
    try {
      const result = await auditQuestionService.getAuditQuestions(
        authTokens.access,
        id, // auditPlanId
        sectionId, // sectionId
        {}, // filters (empty object)
        { page: 1, page_size: 100 } // pagination
      );
      
      if (result.success) {
        setSectionQuestions(prev => ({
          ...prev,
          [sectionId]: result.data.results || []
        }));
      } else {
        showError(result.error || 'Error loading questions');
      }
      setQuestionsLoading(prev => ({ ...prev, [sectionId]: false }));
    } catch (error) {
      console.error('Error fetching questions:', error);
      showError('Error loading questions');
      setQuestionsLoading(prev => ({ ...prev, [sectionId]: false }));
    }
  };

  const refreshQuestions = async (sectionId) => {
    setQuestionsLoading(prev => ({ ...prev, [sectionId]: true }));
    
    try {
      const result = await auditQuestionService.getAuditQuestions(
        authTokens.access,
        id, // auditPlanId
        sectionId, // sectionId
        {}, // filters (empty object)
        { page: 1, page_size: 100 } // pagination
      );
      
      if (result.success) {
        setSectionQuestions(prev => ({
          ...prev,
          [sectionId]: result.data.results || []
        }));
        showSuccess('Questions refreshed successfully');
      } else {
        showError(result.error || 'Error refreshing questions');
      }
      setQuestionsLoading(prev => ({ ...prev, [sectionId]: false }));
    } catch (error) {
      console.error('Error refreshing questions:', error);
      showError('Error refreshing questions');
      setQuestionsLoading(prev => ({ ...prev, [sectionId]: false }));
    }
  };

  const fetchRecommendations = async (questionId, sectionId) => {
    if (showRecommendations[questionId]) {
      setShowRecommendations(prev => ({ ...prev, [questionId]: false }));
      return;
    }

    setRecommendationsLoading(prev => ({ ...prev, [questionId]: true }));
    setShowRecommendations(prev => ({ ...prev, [questionId]: true }));
    
    try {
      const result = await recommendationService.getRecommendations(
        authTokens.access,
        id, // auditPlanId
        sectionId, // sectionId
        questionId, // questionId
        {}, // filters (empty object)
        { page: 1, page_size: 100 } // pagination
      );
      
      if (result.success) {
        setQuestionRecommendations(prev => ({
          ...prev,
          [questionId]: result.data.results || []
        }));
      } else {
        showError(result.error || 'Error loading recommendations');
      }
      setRecommendationsLoading(prev => ({ ...prev, [questionId]: false }));
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      showError('Error loading recommendations');
      setRecommendationsLoading(prev => ({ ...prev, [questionId]: false }));
    }
  };

  const refreshRecommendations = async (questionId, sectionId) => {
    setRecommendationsLoading(prev => ({ ...prev, [questionId]: true }));
    
    try {
      const result = await recommendationService.getRecommendations(
        authTokens.access,
        id, // auditPlanId
        sectionId, // sectionId
        questionId, // questionId
        {}, // filters (empty object)
        { page: 1, page_size: 100 } // pagination
      );
      
      if (result.success) {
        setQuestionRecommendations(prev => ({
          ...prev,
          [questionId]: result.data.results || []
        }));
        showSuccess('Recommendations refreshed successfully');
      } else {
        showError(result.error || 'Error refreshing recommendations');
      }
      setRecommendationsLoading(prev => ({ ...prev, [questionId]: false }));
    } catch (error) {
      console.error('Error refreshing recommendations:', error);
      showError('Error refreshing recommendations');
      setRecommendationsLoading(prev => ({ ...prev, [questionId]: false }));
    }
  };

  const fetchFollowUps = async (recommendationId) => {
    if (showFollowUps[recommendationId]) {
      setShowFollowUps(prev => ({ ...prev, [recommendationId]: false }));
      return;
    }

    setFollowUpsLoading(prev => ({ ...prev, [recommendationId]: true }));
    setShowFollowUps(prev => ({ ...prev, [recommendationId]: true }));
    
    try {
      const result = await recommendationService.getRecommendationFollowUpsForManager(
        authTokens.access,
        recommendationId,
        {},
        { page: 1, page_size: 100 }
      );
      
      if (result.success) {
        setRecommendationFollowUps(prev => ({
          ...prev,
          [recommendationId]: result.data.results || []
        }));
      } else {
        showError(result.error || 'Error loading follow-ups');
      }
      setFollowUpsLoading(prev => ({ ...prev, [recommendationId]: false }));
    } catch (error) {
      console.error('Error fetching follow-ups:', error);
      showError('Error loading follow-ups');
      setFollowUpsLoading(prev => ({ ...prev, [recommendationId]: false }));
    }
  };

  const refreshFollowUps = async (recommendationId) => {
    setFollowUpsLoading(prev => ({ ...prev, [recommendationId]: true }));
    
    try {
      const result = await recommendationService.getRecommendationFollowUpsForManager(
        authTokens.access,
        recommendationId,
        {},
        { page: 1, page_size: 100 }
      );
      
      if (result.success) {
        setRecommendationFollowUps(prev => ({
          ...prev,
          [recommendationId]: result.data.results || []
        }));
        showSuccess('Follow-ups refreshed successfully');
      } else {
        showError(result.error || 'Error refreshing follow-ups');
      }
      setFollowUpsLoading(prev => ({ ...prev, [recommendationId]: false }));
    } catch (error) {
      console.error('Error refreshing follow-ups:', error);
      showError('Error refreshing follow-ups');
      setFollowUpsLoading(prev => ({ ...prev, [recommendationId]: false }));
    }
  };

  const handleExportPDF = async () => {
    try {
      showSuccess('Generating PDF...');
      const result = await exportAuditPlanService.exportAuditPlanPDF(authTokens.access, id);
      
      if (result.success) {
        showSuccess('PDF downloaded successfully');
      } else {
        showError(result.error || 'Error exporting PDF');
      }
    } catch (error) {
      console.error('Error exporting PDF:', error);
      showError('Error exporting PDF. Please try again.');
    }
  };

  const handleDownloadAttachment = async (attachmentId, fileName) => {
    try {
      showSuccess(`Downloading ${fileName}...`);
      const result = await attachmentService.downloadAuditPlanAttachment(authTokens.access, attachmentId);
      
      if (result.success) {
        showSuccess('File downloaded successfully');
      } else {
        showError(result.error || 'Error downloading file');
      }
    } catch (error) {
      console.error('Error downloading attachment:', error);
      showError('Error downloading file. Please try again.');
    }
  };

  const handleDownloadFollowupAttachment = async (attachmentId, fileName) => {
    setDownloadingAttachments(prev => ({ ...prev, [attachmentId]: true }));
    
    try {
      showSuccess(`Téléchargement de ${fileName}...`);
      const result = await followupAttachmentService.downloadAttachment(authTokens.access, attachmentId, fileName);
      
      if (result.success) {
        showSuccess('Fichier téléchargé avec succès');
      } else {
        showError(result.error || 'Erreur lors du téléchargement du fichier');
      }
    } catch (error) {
      console.error('Error downloading followup attachment:', error);
      showError('Erreur lors du téléchargement du fichier. Veuillez réessayer.');
    } finally {
      setDownloadingAttachments(prev => ({ ...prev, [attachmentId]: false }));
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' },
      in_progress: { bg: 'bg-green-100', text: 'text-green-800', label: 'In Progress' },
      completed: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Completed' },
      closed: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Closed' },
      achieved: { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Achieved' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getPartyRoleBadge = (role) => {
    const roleConfig = {
      auditor: { bg: 'bg-green-100', text: 'text-green-800', label: 'Auditeur' },
      auditee: { bg: 'bg-green-100', text: 'text-green-800', label: 'Audité' },
      participant: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Participant' },
      approver: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Approbateur' }
    };

    const config = roleConfig[role] || roleConfig.participant;
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getRiskProbabilityBadge = (probability) => {
    const probabilityConfig = {
      'critical': { bg: 'bg-red-100', text: 'text-red-800', label: 'Critique' },
      'critique': { bg: 'bg-red-100', text: 'text-red-800', label: 'Critique' },
      'high': { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Élevé' },
      'majeur': { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Élevé' },
      'medium': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Moyen' },
      'normal': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Moyen' },
      'low': { bg: 'bg-green-100', text: 'text-green-800', label: 'Mineur' },
      'mineur': { bg: 'bg-green-100', text: 'text-green-800', label: 'Mineur' }
    };

    const config = probabilityConfig[probability] || probabilityConfig['medium'];
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const handleExportQuestion = async (questionId) => {
    setExportingQuestions(prev => ({ ...prev, [questionId]: true }));
    
    try {
      const result = await exportQuestionService.exportQuestionPDF(authTokens.access, questionId);
      
      if (result.success) {
        showSuccess('Question exportée avec succès');
      } else {
        showError(result.error || 'Erreur lors de l\'exportation de la question');
      }
    } catch (error) {
      console.error('Error exporting question:', error);
      showError('Erreur lors de l\'exportation de la question');
    } finally {
      setExportingQuestions(prev => ({ ...prev, [questionId]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des détails du plan d'audit...</p>
        </div>
      </div>
    );
  }

  if (!auditPlan) {
    return (
      <div className="text-center py-12">
        <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">Plan d'audit non trouvé</p>
        <Link
          to={-1}
          className="inline-flex items-center mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour aux plans d'audit
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Modals */}
      <ReleaseModal
        isOpen={showReleaseModal}
        onClose={() => setShowReleaseModal(false)}
        planId={id}
        onSuccess={fetchAuditPlan}
      />
      <CloseModal
        isOpen={showCloseModal}
        onClose={() => setShowCloseModal(false)}
        planId={id}
        onSuccess={fetchAuditPlan}
      />
      <AchieveModal
        isOpen={showAchieveModal}
        onClose={() => setShowAchieveModal(false)}
        planId={id}
        onSuccess={fetchAuditPlan}
      />
      <UnachieveModal
        isOpen={showUnachieveModal}
        onClose={() => setShowUnachieveModal(false)}
        planId={id}
        onSuccess={fetchAuditPlan}
      />
      <ReopenModal
        isOpen={showReopenModal}
        onClose={() => setShowReopenModal(false)}
        planId={id}
        onSuccess={fetchAuditPlan}
      />
      <PublishModal
        isOpen={showPublishModal}
        onClose={() => setShowPublishModal(false)}
        planId={id}
        onSuccess={fetchAuditPlan}
      />
      <PausePublicationModal
        isOpen={showPausePublicationModal}
        onClose={() => setShowPausePublicationModal(false)}
        planId={id}
        onSuccess={fetchAuditPlan}
      />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to={-1}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{auditPlan.title}</h1>
            <p className="text-sm text-gray-600 mt-1">Détails du plan d'audit</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {getStatusBadge(auditPlan.status)}
          
          {/* Workflow Actions for AuditManager */}
          {hasRole('AuditManager') && (
            <>
              {/* Release Button - Only when not released */}
              {!auditPlan.is_released && (
                <button
                  onClick={() => setShowReleaseModal(true)}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Play className="h-4 w-4 mr-2" />
                  Release
                </button>
              )}
              
              {/* Close Button - Only when released, status is completed, and program is opened */}
              {auditPlan.is_released && auditPlan.status !== 'closed' && auditPlan.program?.status === 'opened' && (
                <button
                  onClick={() => setShowCloseModal(true)}
                  className="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Clôturer
                </button>
              )}
              

              
              {/* Achieve Button - Show always except when achieved, and program is opened */}
              {auditPlan.status !== 'achieved' && auditPlan.program?.status === 'opened' && (
                <button
                  onClick={() => setShowAchieveModal(true)}
                  className="inline-flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                >
                  <Award className="h-4 w-4 mr-2" />
                  Archiver
                </button>
              )}
              

              
              {/* Unachieve Button - Only when achieved and program is opened */}
              {auditPlan.status === 'achieved' && auditPlan.program?.status === 'opened' && (
                <button
                  onClick={() => setShowUnachieveModal(true)}
                  className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                >
                  <Award className="h-4 w-4 mr-2" />
                  Désarchiver
                </button>
              )}
              
  
              
              {/* Reopen Button - Only when closed AND program is opened */}
              {auditPlan.status === 'closed' && auditPlan.program?.status === 'opened' && (
                <button
                  onClick={() => setShowReopenModal(true)}
                  className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Rouvrir
                </button>
              )}
              
     
              {/* Publish Button - Only when closed or achieved and not published */}
              {(auditPlan.status === 'closed' || auditPlan.status === 'achieved') && 
                !auditPlan.is_published && (
                  <button
                    onClick={() => setShowPublishModal(true)}
                    className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Publier
                  </button>
              )}
              
              {/* Pause Publication Button - Only when published */}
              {auditPlan.is_published && (
                <button
                  onClick={() => setShowPausePublicationModal(true)}
                  className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                >
                  <Pause className="h-4 w-4 mr-2" />
                  Suspendre la publication
                </button>
              )}
              
              {/* Edit Button - Hide when closed or achieved */}
              {auditPlan.status !== 'closed' && auditPlan.status !== 'achieved' && (
                <Link
                  to={`/dashboard/audit/plans/${auditPlan.id}/edit`}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Modifier
                </Link>
              )}
              
              {/* Export PDF Button */}
              <button
                onClick={handleExportPDF}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Download className="h-4 w-4 mr-2" />
                Export PDF
              </button>
            </>
          )}
        </div>
      </div>

      {/* Main Content - Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <FileText className="h-5 w-5 mr-2 text-green-600" />
              Informations de base
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Objectifs</label>
                <p className="text-sm text-gray-900">{auditPlan.objectives}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Périmètre</label>
                <p className="text-sm text-gray-900">{auditPlan.scope}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Normes de référence</label>
                <p className="text-sm text-gray-900">{auditPlan.reference}</p>
              </div>
              {auditPlan.note && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <p className="text-sm text-gray-900">{auditPlan.note}</p>
                </div>
              )}
            </div>
          </div>

          {/* Parties Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Users className="h-5 w-5 mr-2 text-green-600" />
              Parties impliquées ({auditPlan.parties ? auditPlan.parties.length : 0})
            </h2>
            <div className="space-y-3">
              {auditPlan.parties && auditPlan.parties.length > 0 ? (
                auditPlan.parties.map((party) => (
                  <div
                    key={party.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="h-10 w-10 bg-green-100 rounded-full flex items-center justify-center">
                        <User className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{party.name}</p>
                        <p className="text-xs text-gray-500">{party.email}</p>
                      </div>
                    </div>
                    <div>
                      {getPartyRoleBadge(party.partyRole)}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Users className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm">Aucune partie impliquée</p>
                </div>
              )}
            </div>
          </div>

          {/* Risks Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <AlertTriangle className="h-5 w-5 mr-2 text-green-600" />
              Risques identifiés ({auditPlan.risks ? auditPlan.risks.length : 0})
            </h2>
            <div className="space-y-3">
              {auditPlan.risks && auditPlan.risks.length > 0 ? (
                auditPlan.risks.map((risk) => (
                  <div
                    key={risk.id}
                    className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-sm font-medium text-gray-900">{risk.name}</h3>
                      {getRiskProbabilityBadge(risk.probability)}
                    </div>
                    <p className="text-xs text-gray-600 mb-2">{risk.description}</p>
                    <div className="text-xs text-gray-500">
                      <span className="font-medium">Conséquence:</span> {risk.consequence}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm">Aucun risque identifié</p>
                </div>
              )}
            </div>
          </div>

          {/* Audit Sections - Full Width */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <List className="h-5 w-5 mr-2 text-green-600" />
                Sections d'audit
              </h2>
              <div className="flex items-center space-x-2">
                {showSections && (
                  <button
                    onClick={refreshSections}
                    disabled={sectionsLoading}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Refresh sections"
                  >
                    <RefreshCw className={`h-4 w-4 ${sectionsLoading ? 'animate-spin' : ''}`} />
                  </button>
                )}
                <button
                  onClick={fetchSections}
                  disabled={sectionsLoading}
                  className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sectionsLoading && !showSections ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Chargement...
                    </>
                  ) : (
                    <>
                      {showSections ? (
                        <>
                          <ChevronDown className="h-4 w-4 mr-2" />
                          Masquer les sections
                        </>
                      ) : (
                        <>
                          <ChevronRight className="h-4 w-4 mr-2" />
                          Afficher les sections
                        </>
                      )}
                    </>
                  )}
                </button>
              </div>
            </div>

            {showSections && (
              <div className="mt-4">
                {sectionsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-3"></div>
                      <p className="text-sm text-gray-600">Chargement des sections...</p>
                    </div>
                  </div>
                ) : sections.length > 0 ? (
                  <div className="space-y-4">
                    {sections.map((section) => (
                      <div
                        key={section.id}
                        className="border border-gray-200 rounded-lg p-5 bg-gray-50"
                      >
                        <div className="mb-4">
                          <h3 className="text-lg font-semibold text-gray-900 mb-3">
                            {section.section_name}
                          </h3>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Location */}
                            <div className="flex items-start">
                              <MapPin className="h-4 w-4 mr-2 text-gray-400 mt-0.5" />
                              <div>
                                <span className="text-xs font-medium text-gray-500">Localisation</span>
                                <p className="text-sm text-gray-900">{section.location}</p>
                              </div>
                            </div>

                            {/* Created By */}
                            {section.created_by && (
                              <div className="flex items-start">
                                <User className="h-4 w-4 mr-2 text-gray-400 mt-0.5" />
                                <div>
                                  <span className="text-xs font-medium text-gray-500">Créé par</span>
                                  <p className="text-sm text-gray-900">{section.created_by}</p>
                                </div>
                              </div>
                            )}

                            {/* Created At */}
                            <div className="flex items-start">
                              <Calendar className="h-4 w-4 mr-2 text-gray-400 mt-0.5" />
                              <div>
                                <span className="text-xs font-medium text-gray-500">Créé le</span>
                                <p className="text-sm text-gray-900">
                                  {new Date(section.created_at).toLocaleDateString('fr-FR', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                              </div>
                            </div>

                            {/* Last Updated By */}
                            {section.last_updated_by && (
                              <div className="flex items-start">
                                <User className="h-4 w-4 mr-2 text-gray-400 mt-0.5" />
                                <div>
                                  <span className="text-xs font-medium text-gray-500">Dernière mise à jour par</span>
                                  <p className="text-sm text-gray-900">{section.last_updated_by}</p>
                                </div>
                              </div>
                            )}

                            {/* Updated At */}
                            {section.updated_at && section.updated_at !== section.created_at && (
                              <div className="flex items-start">
                                <Clock className="h-4 w-4 mr-2 text-gray-400 mt-0.5" />
                                <div>
                                  <span className="text-xs font-medium text-gray-500">Dernière mise à jour le</span>
                                  <p className="text-sm text-gray-900">
                                    {new Date(section.updated_at).toLocaleDateString('fr-FR', {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Questions Section */}
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-semibold text-gray-700 flex items-center">
                              <HelpCircle className="h-4 w-4 mr-2 text-green-600" />
                              Questions d'audit
                            </h4>
                            <div className="flex items-center space-x-2">
                              {showQuestions[section.id] && sectionQuestions[section.id] && (
                                <button
                                  onClick={() => refreshQuestions(section.id)}
                                  disabled={questionsLoading[section.id]}
                                  className="inline-flex items-center px-2 py-1 border border-gray-300 text-gray-700 text-xs rounded hover:bg-gray-50 transition-colors disabled:opacity-50"
                                  title="Refresh questions"
                                >
                                  <RefreshCw className={`h-3 w-3 ${questionsLoading[section.id] ? 'animate-spin' : ''}`} />
                                </button>
                              )}
                              <button
                                onClick={() => fetchQuestions(section.id)}
                                disabled={questionsLoading[section.id]}
                                className="inline-flex items-center px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors disabled:opacity-50"
                              >
                                {questionsLoading[section.id] && !showQuestions[section.id] ? (
                                  <>
                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                                    Chargement...
                                  </>
                                ) : (
                                  <>
                                    {showQuestions[section.id] ? (
                                      <>
                                        <ChevronDown className="h-3 w-3 mr-1" />
                                        Masquer
                                      </>
                                    ) : (
                                      <>
                                        <ChevronRight className="h-3 w-3 mr-1" />
                                        Afficher
                                      </>
                                    )}
                                  </>
                                )}
                              </button>
                            </div>
                          </div>

                          {showQuestions[section.id] && (
                            <div className="mt-3">
                              {questionsLoading[section.id] ? (
                                <div className="flex items-center justify-center py-8">
                                  <div className="text-center">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600 mx-auto mb-2"></div>
                                    <p className="text-xs text-gray-600">Chargement des questions...</p>
                                  </div>
                                </div>
                              ) : sectionQuestions[section.id] && sectionQuestions[section.id].length > 0 ? (
                                <div className="space-y-3">
                                  {sectionQuestions[section.id].map((question) => (
                                    <div
                                      key={question.id}
                                      className="border border-gray-200 rounded-lg p-4 bg-white"
                                    >
                                      <div className="flex items-start justify-between mb-3">
                                        <h5 className="text-sm font-semibold text-gray-900 flex-1">
                                          {question.question_name}
                                        </h5>
                                        <button
                                          onClick={() => handleExportQuestion(question.id)}
                                          disabled={exportingQuestions[question.id]}
                                          className="ml-3 inline-flex items-center px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                                          title="Exporter la question en PDF"
                                        >
                                          {exportingQuestions[question.id] ? (
                                            <>
                                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                                              Export...
                                            </>
                                          ) : (
                                            <>
                                              <Download className="h-3 w-3 mr-1" />
                                              PDF
                                            </>
                                          )}
                                        </button>
                                      </div>
                                      
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                                        <div>
                                          <span className="font-medium text-gray-500">Localisation:</span>
                                          <p className="text-gray-900">{question.location}</p>
                                        </div>
                                        <div>
                                          <span className="font-medium text-gray-500">Statut:</span>
                                          <span className={`ml-2 px-2 py-0.5 rounded text-xs ${
                                            question.status === 'completed' ? 'bg-purple-100 text-purple-800' :
                                            question.status === 'in_progress' ? 'bg-green-100 text-green-800' :
                                            'bg-yellow-100 text-yellow-800'
                                          }`}>
                                            {question.status_display || question.status}
                                          </span>
                                        </div>
                                        
                                        {question.risk_name && (
                                          <div>
                                            <span className="font-medium text-gray-500">Risque associé:</span>
                                            <p className="text-gray-900">{question.risk_name}</p>
                                          </div>
                                        )}
                                        
                                        {question.question_risks && question.question_risks.length > 0 && (
                                          <div className="md:col-span-2">
                                            <span className="font-medium text-gray-500">Risques associés ({question.question_risks.length}):</span>
                                            <div className="mt-2 space-y-2">
                                              {question.question_risks.map((questionRisk) => (
                                                <div key={questionRisk.id} className="bg-red-50 border border-red-200 rounded-lg p-3">
                                                  <div className="flex items-start justify-between mb-2">
                                                    <h6 className="text-sm font-semibold text-gray-900">{questionRisk.risk_name}</h6>
                                                    {getRiskProbabilityBadge(questionRisk.probability)}
                                                  </div>
                                                  <p className="text-xs text-gray-600 mb-2">{questionRisk.description}</p>
                                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                                                    <div>
                                                      <span className="font-medium text-gray-500">Date du risque:</span>
                                                      <p className="text-gray-900">
                                                        {questionRisk.risk_date 
                                                          ? new Date(questionRisk.risk_date).toLocaleDateString('fr-FR')
                                                          : 'Non définie'
                                                        }
                                                      </p>
                                                    </div>
                                                    <div>
                                                      <span className="font-medium text-gray-500">Statut:</span>
                                                      <span className={`ml-2 px-2 py-0.5 rounded text-xs ${
                                                        questionRisk.status === 'approved' ? 'bg-green-100 text-green-800' :
                                                        questionRisk.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                                        'bg-yellow-100 text-yellow-800'
                                                      }`}>
                                                        {questionRisk.status}
                                                      </span>
                                                    </div>
                                                  </div>
                                                  {questionRisk.consequence && (
                                                    <div className="mt-2">
                                                      <span className="font-medium text-gray-500">Conséquence:</span>
                                                      <p className="text-xs text-gray-900 mt-1">{questionRisk.consequence}</p>
                                                    </div>
                                                  )}
                                                  {questionRisk.actions && questionRisk.actions.length > 0 && (
                                                    <div className="mt-2">
                                                      <span className="font-medium text-gray-500">Actions ({questionRisk.actions.length}):</span>
                                                      <div className="mt-1 space-y-1">
                                                        {questionRisk.actions.map((action) => (
                                                          <div key={action.id} className="bg-white border border-gray-200 rounded p-2">
                                                            <p className="text-xs text-gray-900">{action.action}</p>
                                                            <div className="flex items-center justify-between mt-1 text-xs text-gray-500">
                                                              <span>
                                                                Échéance: {action.due_date 
                                                                  ? new Date(action.due_date).toLocaleDateString('fr-FR')
                                                                  : 'Non définie'
                                                                }
                                                              </span>
                                                              <span className={`px-2 py-0.5 rounded ${
                                                                action.is_completed ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                                              }`}>
                                                                {action.is_completed ? 'Terminé' : 'En cours'}
                                                              </span>
                                                            </div>
                                                          </div>
                                                        ))}
                                                      </div>
                                                    </div>
                                                  )}
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                        
                                        {question.interview_result && (
                                          <div className="md:col-span-2">
                                            <span className="font-medium text-gray-500">Résultat de l'entretien:</span>
                                            <p className="text-gray-900 mt-1">{question.interview_result}</p>
                                          </div>
                                        )}
                                        
                                        <div>
                                          <span className="font-medium text-gray-500">Confirmation audité:</span>
                                          <span className={`ml-2 px-2 py-0.5 rounded text-xs ${
                                            question.auditee_response_confirmation === 'confirmed' ? 'bg-green-100 text-green-800' :
                                            question.auditee_response_confirmation === 'rejected' ? 'bg-red-100 text-red-800' :
                                            'bg-yellow-100 text-yellow-800'
                                          }`}>
                                            {question.auditee_confirmation_display || question.auditee_response_confirmation}
                                          </span>
                                        </div>
                                        
                                        <div>
                                          <span className="font-medium text-gray-500">Efficacité de l'observation:</span>
                                          <span className={`ml-2 px-2 py-0.5 rounded text-xs ${
                                            question.observation_effectiveness ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                          }`}>
                                            {question.observation_effectiveness ? 'Oui' : 'Non'}
                                          </span>
                                        </div>
                                        
                                        {/* Dates */}
                                        <div>
                                          <span className="font-medium text-gray-500">Créé le:</span>
                                          <p className="text-gray-900">
                                            {question.created_at 
                                              ? new Date(question.created_at).toLocaleDateString('fr-FR', {
                                                  year: 'numeric',
                                                  month: 'short',
                                                  day: 'numeric'
                                                })
                                              : 'N/A'
                                            }
                                          </p>
                                        </div>
                                        
                                        <div>
                                          <span className="font-medium text-gray-500">Mis à jour le:</span>
                                          <p className="text-gray-900">
                                            {question.updated_at 
                                              ? new Date(question.updated_at).toLocaleDateString('fr-FR', {
                                                  year: 'numeric',
                                                  month: 'short',
                                                  day: 'numeric'
                                                })
                                              : 'N/A'
                                            }
                                          </p>
                                        </div>
                                      </div>

                                      {/* Detailed sections for observations, KPIs, and attachments */}
                                      <div className="mt-4 space-y-3">
                                        {/* Initial Observations */}
                                        {question.initial_observations && question.initial_observations.length > 0 && (
                                          <div className="border-t border-gray-200 pt-3">
                                            <h6 className="text-xs font-semibold text-gray-700 mb-2">
                                              Observations initiales ({question.initial_observations.length})
                                            </h6>
                                            <div className="space-y-2">
                                              {question.initial_observations.map((obs, idx) => (
                                                <div key={obs.id || idx} className="bg-blue-50 p-2 rounded text-xs">
                                                  <p className="text-gray-900">{obs.observation}</p>
                                                  <p className="text-gray-500 mt-1">
                                                    Par {obs.created_by_name} - {new Date(obs.created_at).toLocaleDateString('fr-FR')}
                                                  </p>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}

                                        {/* Observations */}
                                        {question.observations && question.observations.length > 0 && (
                                          <div className="border-t border-gray-200 pt-3">
                                            <h6 className="text-xs font-semibold text-gray-700 mb-2">
                                              Observations ({question.observations.length})
                                            </h6>
                                            <div className="space-y-2">
                                              {question.observations.map((obs, idx) => (
                                                <div key={obs.id || idx} className="bg-purple-50 p-2 rounded text-xs">
                                                  <p className="text-gray-900">{obs.observation}</p>
                                                  <p className="text-gray-500 mt-1">
                                                    Par {obs.created_by_name} - {new Date(obs.created_at).toLocaleDateString('fr-FR')}
                                                  </p>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}

                                        {/* KPIs */}
                                        {question.kpis && question.kpis.length > 0 && (
                                          <div className="border-t border-gray-200 pt-3">
                                            <h6 className="text-xs font-semibold text-gray-700 mb-2">
                                              KPIs ({question.kpis.length})
                                            </h6>
                                            <div className="space-y-2">
                                              {question.kpis.map((kpi, idx) => (
                                                <div key={kpi.id || idx} className="bg-green-50 p-2 rounded text-xs">
                                                  <p className="font-semibold text-gray-900">{kpi.kpi_name}</p>
                                                  <div className="grid grid-cols-2 gap-2 mt-1">
                                                    <div>
                                                      <span className="text-gray-500">Valeur:</span>
                                                      <span className="ml-1 text-gray-900">{kpi.kpi_value}</span>
                                                    </div>
                                                    <div>
                                                      <span className="text-gray-500">Cible:</span>
                                                      <span className="ml-1 text-gray-900">{kpi.kpi_target}</span>
                                                    </div>
                                                  </div>
                                                  {kpi.kpi_description && (
                                                    <p className="text-gray-600 mt-1">{kpi.kpi_description}</p>
                                                  )}
                                                  <p className="text-gray-500 mt-1">
                                                    Par {kpi.created_by_name} - {new Date(kpi.created_at).toLocaleDateString('fr-FR')}
                                                  </p>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}

                                        {/* Attachments */}
                                        {question.attachments && question.attachments.length > 0 && (
                                          <div className="border-t border-gray-200 pt-3">
                                            <h6 className="text-xs font-semibold text-gray-700 mb-2">
                                              Pièces jointes ({question.attachments.length})
                                            </h6>
                                            <div className="space-y-2">
                                              {question.attachments.map((attachment, idx) => (
                                                <div key={attachment.id || idx} className="bg-orange-50 p-3 rounded text-xs">
                                                  <div className="flex items-start justify-between">
                                                    <div className="flex-1">
                                                      <p className="font-semibold text-gray-900">{attachment.file_name}</p>
                                                      {attachment.description && (
                                                        <p className="text-gray-600 mt-1">{attachment.description}</p>
                                                      )}
                                                      <p className="text-gray-500 mt-1">
                                                        Taille: {attachment.file_size ? (attachment.file_size / 1024).toFixed(2) : 'N/A'} KB
                                                        {attachment.uploaded_by_name && ` - Par ${attachment.uploaded_by_name}`}
                                                        {attachment.created_at && ` - ${new Date(attachment.created_at).toLocaleDateString('fr-FR')}`}
                                                      </p>
                                                    </div>
                                                    <button
                                                      onClick={() => handleDownloadAttachment(attachment.id, attachment.file_name)}
                                                      className="ml-3 inline-flex items-center px-3 py-1.5 bg-orange-600 text-white rounded hover:bg-orange-700 transition-colors whitespace-nowrap"
                                                    >
                                                      <Download className="h-3 w-3 mr-1" />
                                                      Télécharger
                                                    </button>
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </div>

                                      {/* Question Recommendations Section */}
                                      {question.recommendations && question.recommendations.length > 0 && (
                                        <div className="mt-4 pt-4 border-t border-gray-200">
                                          <h6 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                                            <Lightbulb className="h-4 w-4 mr-2 text-blue-600" />
                                            Recommandations de la question ({question.recommendations.length})
                                          </h6>
                                          <div className="space-y-3">
                                            {question.recommendations.map((recommendation) => (
                                              <div key={recommendation.id} className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                                <div className="flex items-start justify-between mb-2">
                                                  <p className="text-sm text-gray-900 flex-1">{recommendation.recommendation}</p>
                                                  <span className={`ml-3 px-2 py-1 rounded text-xs font-medium flex-shrink-0 ${
                                                    recommendation.priority === 'critical' ? 'bg-red-100 text-red-800' :
                                                    recommendation.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                                                    recommendation.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                                    'bg-green-100 text-green-800'
                                                  }`}>
                                                    {recommendation.priority_display || recommendation.priority}
                                                  </span>
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                  Créé le {new Date(recommendation.created_at).toLocaleDateString('fr-FR', {
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: 'numeric'
                                                  })}
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}

                                      {/* Recommendations Section */}
                                      <div className="mt-3 pt-3 border-t border-gray-200">
                                        <div className="flex items-center justify-between mb-2">
                                          <h6 className="text-xs font-semibold text-gray-700 flex items-center">
                                            <Lightbulb className="h-3 w-3 mr-1 text-yellow-600" />
                                            Actions de recommandation
                                          </h6>
                                          <div className="flex items-center space-x-1">
                                            {showRecommendations[question.id] && questionRecommendations[question.id] && (
                                              <button
                                                onClick={() => refreshRecommendations(question.id, section.id)}
                                                disabled={recommendationsLoading[question.id]}
                                                className="inline-flex items-center px-2 py-0.5 border border-gray-300 text-gray-700 text-xs rounded hover:bg-gray-50 transition-colors disabled:opacity-50"
                                                title="Refresh recommendations"
                                              >
                                                <RefreshCw className={`h-3 w-3 ${recommendationsLoading[question.id] ? 'animate-spin' : ''}`} />
                                              </button>
                                            )}
                                            <button
                                              onClick={() => fetchRecommendations(question.id, section.id)}
                                              disabled={recommendationsLoading[question.id]}
                                              className="inline-flex items-center px-2 py-0.5 bg-yellow-600 text-white text-xs rounded hover:bg-yellow-700 transition-colors disabled:opacity-50"
                                            >
                                              {recommendationsLoading[question.id] && !showRecommendations[question.id] ? (
                                                <>
                                                  <div className="animate-spin rounded-full h-2 w-2 border-b-2 border-white mr-1"></div>
                                                  Chargement...
                                                </>
                                              ) : (
                                                <>
                                                  {showRecommendations[question.id] ? (
                                                    <>
                                                      <ChevronDown className="h-3 w-3 mr-1" />
                                                      Masquer
                                                    </>
                                                  ) : (
                                                    <>
                                                      <ChevronRight className="h-3 w-3 mr-1" />
                                                      Afficher
                                                    </>
                                                  )}
                                                </>
                                              )}
                                            </button>
                                          </div>
                                        </div>

                                        {showRecommendations[question.id] && (
                                          <div className="mt-2">
                                            {recommendationsLoading[question.id] ? (
                                              <div className="flex items-center justify-center py-6">
                                                <div className="text-center">
                                                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-yellow-600 mx-auto mb-2"></div>
                                                  <p className="text-xs text-gray-600">Chargement des recommandations...</p>
                                                </div>
                                              </div>
                                            ) : questionRecommendations[question.id] && questionRecommendations[question.id].length > 0 ? (
                                              <div className="space-y-2">
                                                {questionRecommendations[question.id].map((recommendation) => (
                                                  <div
                                                    key={recommendation.id}
                                                    className="border border-yellow-200 rounded-lg p-3 bg-yellow-50"
                                                  >
                                                    <div className="text-xs space-y-2">
                                                      <div>
                                                        <span className="font-medium text-gray-500">Propriétaire:</span>
                                                        <p className="text-gray-900">{recommendation.owner_name || 'Non assigné'}</p>
                                                      </div>
                                                      <div>
                                                        <span className="font-medium text-gray-500">Statut:</span>
                                                        <span className={`ml-2 px-2 py-0.5 rounded text-xs ${
                                                          recommendation.status === 'completed' ? 'bg-green-100 text-green-800' :
                                                          recommendation.status === 'in_progress' ? 'bg-green-100 text-green-800' :
                                                          'bg-gray-100 text-gray-800'
                                                        }`}>
                                                          {recommendation.status}
                                                        </span>
                                                      </div>
                                                      <div>
                                                        <span className="font-medium text-gray-500">Date cible:</span>
                                                        <p className="text-gray-900">
                                                          {recommendation.delay_of_recommendation 
                                                            ? new Date(recommendation.delay_of_recommendation).toLocaleDateString('fr-FR')
                                                            : 'Non définie'
                                                          }
                                                        </p>
                                                      </div>
                                                      <div className="grid grid-cols-2 gap-2">
                                                        <div>
                                                          <span className="font-medium text-gray-500">Créé:</span>
                                                          <p className="text-gray-900">
                                                            {recommendation.created_at 
                                                              ? new Date(recommendation.created_at).toLocaleDateString('fr-FR')
                                                              : 'Inconnu'
                                                            }
                                                          </p>
                                                        </div>
                                                        <div>
                                                          <span className="font-medium text-gray-500">Mis à jour:</span>
                                                          <p className="text-gray-900">
                                                            {recommendation.updated_at 
                                                              ? new Date(recommendation.updated_at).toLocaleDateString('fr-FR')
                                                              : 'Inconnu'
                                                            }
                                                          </p>
                                                        </div>
                                                      </div>
                                                      {recommendation.elements_of_recommendation && (
                                                        <div>
                                                          <span className="font-medium text-gray-500">Éléments:</span>
                                                          <p className="text-gray-900 mt-1">{recommendation.elements_of_recommendation}</p>
                                                        </div>
                                                      )}
                                                    </div>

                                                    {/* Follow-ups Section */}
                                                    <div className="mt-2 pt-2 border-t border-yellow-300">
                                                      <div className="flex items-center justify-between mb-2">
                                                        <h6 className="text-xs font-semibold text-gray-700 flex items-center">
                                                          <TrendingUp className="h-3 w-3 mr-1 text-green-600" />
                                                          Suivis
                                                        </h6>
                                                        <div className="flex items-center space-x-1">
                                                          {showFollowUps[recommendation.id] && recommendationFollowUps[recommendation.id] && (
                                                            <button
                                                              onClick={() => refreshFollowUps(recommendation.id)}
                                                              disabled={followUpsLoading[recommendation.id]}
                                                              className="inline-flex items-center px-2 py-0.5 border border-gray-300 text-gray-700 text-xs rounded hover:bg-gray-50 transition-colors disabled:opacity-50"
                                                              title="Refresh follow-ups"
                                                            >
                                                              <RefreshCw className={`h-2 w-2 ${followUpsLoading[recommendation.id] ? 'animate-spin' : ''}`} />
                                                            </button>
                                                          )}
                                                          <button
                                                            onClick={() => fetchFollowUps(recommendation.id)}
                                                            disabled={followUpsLoading[recommendation.id]}
                                                            className="inline-flex items-center px-2 py-0.5 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors disabled:opacity-50"
                                                          >
                                                            {followUpsLoading[recommendation.id] && !showFollowUps[recommendation.id] ? (
                                                              <>
                                                                <div className="animate-spin rounded-full h-2 w-2 border-b-2 border-white mr-1"></div>
                                                                Chargement...
                                                              </>
                                                            ) : (
                                                              <>
                                                                {showFollowUps[recommendation.id] ? (
                                                                  <>
                                                                    <ChevronDown className="h-2 w-2 mr-1" />
                                                                    Masquer
                                                                  </>
                                                                ) : (
                                                                  <>
                                                                    <ChevronRight className="h-2 w-2 mr-1" />
                                                                    Afficher
                                                                  </>
                                                                )}
                                                              </>
                                                            )}
                                                          </button>
                                                        </div>
                                                      </div>

                                                      {showFollowUps[recommendation.id] && (
                                                        <div className="mt-2">
                                                          {followUpsLoading[recommendation.id] ? (
                                                            <div className="flex items-center justify-center py-4">
                                                              <div className="text-center">
                                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mx-auto mb-1"></div>
                                                                <p className="text-xs text-gray-600">Chargement des suivis...</p>
                                                              </div>
                                                            </div>
                                                          ) : recommendationFollowUps[recommendation.id] && recommendationFollowUps[recommendation.id].length > 0 ? (
                                                            <div className="space-y-2">
                                                              {recommendationFollowUps[recommendation.id].map((followUp) => (
                                                                <div
                                                                  key={followUp.id}
                                                                  className="border border-green-200 rounded p-2 bg-green-50"
                                                                >
                                                                  <div className="text-xs space-y-1">
                                                                    <div className="flex items-center justify-between">
                                                                      <span className="font-medium text-gray-500">Progression:</span>
                                                                      <span className="font-semibold text-green-700">{followUp.progression_percentage}%</span>
                                                                    </div>
                                                                    <div>
                                                                      <span className="font-medium text-gray-500">Statut:</span>
                                                                      <span className="ml-2 text-gray-900">{followUp.recommendation_status_selected}</span>
                                                                    </div>
                                                                    {followUp.notes && (
                                                                      <div>
                                                                        <span className="font-medium text-gray-500">Notes:</span>
                                                                        <p className="text-gray-900 mt-1">{followUp.notes}</p>
                                                                      </div>
                                                                    )}
                                                                    {followUp.attachments && followUp.attachments.length > 0 && (
                                                                      <div>
                                                                        <span className="font-medium text-gray-500">Pièces jointes ({followUp.attachments.length}):</span>
                                                                        <div className="mt-1 space-y-1">
                                                                          {followUp.attachments.map((attachment) => (
                                                                            <div key={attachment.id} className="flex items-center justify-between bg-white p-2 rounded border border-gray-200">
                                                                              <div className="flex-1 min-w-0">
                                                                                <p className="text-xs font-medium text-gray-900 truncate">{attachment.file_name}</p>
                                                                                {attachment.file_size && (
                                                                                  <p className="text-xs text-gray-500">
                                                                                    {(attachment.file_size / 1024).toFixed(2)} KB
                                                                                  </p>
                                                                                )}
                                                                              </div>
                                                                              <button
                                                                                onClick={() => handleDownloadFollowupAttachment(attachment.id, attachment.file_name)}
                                                                                disabled={downloadingAttachments[attachment.id]}
                                                                                className="ml-2 inline-flex items-center px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors disabled:opacity-50"
                                                                              >
                                                                                {downloadingAttachments[attachment.id] ? (
                                                                                  <>
                                                                                    <div className="animate-spin rounded-full h-2 w-2 border-b-2 border-white mr-1"></div>
                                                                                    Téléchargement...
                                                                                  </>
                                                                                ) : (
                                                                                  <>
                                                                                    <Download className="h-2 w-2 mr-1" />
                                                                                    Télécharger
                                                                                  </>
                                                                                )}
                                                                              </button>
                                                                            </div>
                                                                          ))}
                                                                        </div>
                                                                      </div>
                                                                    )}
                                                                    <div className="text-xs text-gray-400">
                                                                      {new Date(followUp.created_at).toLocaleDateString('fr-FR')}
                                                                    </div>
                                                                  </div>
                                                                </div>
                                                              ))}
                                                            </div>
                                                          ) : (
                                                            <div className="text-center py-4 text-gray-500">
                                                              <TrendingUp className="h-6 w-6 mx-auto mb-1 text-gray-400" />
                                                              <p className="text-xs">Aucun suivi pour le moment</p>
                                                            </div>
                                                          )}
                                                        </div>
                                                      )}
                                                    </div>
                                                  </div>
                                                ))}
                                              </div>
                                            ) : (
                                              <div className="text-center py-6 text-gray-500">
                                                <Lightbulb className="h-6 w-6 mx-auto mb-1 text-gray-400" />
                                                <p className="text-xs">Aucune action de recommandation pour le moment</p>
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-center py-8 text-gray-500">
                                  <HelpCircle className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                                  <p className="text-xs font-medium">Aucune question pour le moment</p>
                                  <p className="text-xs text-gray-400 mt-1">Les questions apparaîtront ici une fois créées</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <List className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                    <p className="text-sm font-medium">Aucune section d'audit pour le moment</p>
                    <p className="text-xs text-gray-400 mt-1">Les sections apparaîtront ici une fois créées</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Meta Information */}
        <div className="lg:col-span-1 space-y-6">
          {/* Program Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Building2 className="h-5 w-5 mr-2 text-green-600" />
              Programme d'audit
            </h2>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Titre du programme</p>
                <Link
                  to={`/dashboard/audit/programs/${auditPlan.program?.id || '#'}`}
                  className="text-sm font-medium text-green-600 hover:underline"
                >
                  {auditPlan.program?.title || 'Aucun programme'}
                </Link>
              </div>

              {auditPlan.program?.objective && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Objectif du programme</p>
                  <p className="text-sm text-gray-900">{auditPlan.program.objective}</p>
                </div>
              )}

              {auditPlan.program?.scope && auditPlan.program.scope.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Périmètre du programme</p>
                  <div className="space-y-1">
                    {auditPlan.program.scope.map((scopeItem, index) => (
                      <div key={index} className="flex items-center text-sm text-gray-900">
                        <span className="w-2 h-2 bg-green-600 rounded-full mr-2"></span>
                        {scopeItem}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Timeline Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Calendar className="h-5 w-5 mr-2 text-green-600" />
              Chronologie
            </h2>
            <div className="space-y-3">
              {auditPlan.release_date && (
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
                      <Calendar className="h-4 w-4 text-green-600" />
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-xs font-medium text-gray-700">Publié</p>
                    <p className="text-sm text-gray-900">{new Date(auditPlan.release_date).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                  </div>
                </div>
              )}
              {auditPlan.start_date && (
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
                      <Calendar className="h-4 w-4 text-green-600" />
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-xs font-medium text-gray-700">Démarré</p>
                    <p className="text-sm text-gray-900">{new Date(auditPlan.start_date).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                  </div>
                </div>
              )}
              {auditPlan.end_date && (
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 bg-purple-100 rounded-full flex items-center justify-center">
                      <Calendar className="h-4 w-4 text-purple-600" />
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-xs font-medium text-gray-700">Terminé</p>
                    <p className="text-sm text-gray-900">{new Date(auditPlan.end_date).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                  </div>
                </div>
              )}
              {auditPlan.closed_date && (
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 bg-gray-100 rounded-full flex items-center justify-center">
                      <Calendar className="h-4 w-4 text-gray-600" />
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-xs font-medium text-gray-700">Clôturé</p>
                    <p className="text-sm text-gray-900">{new Date(auditPlan.closed_date).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                  </div>
                </div>
              )}
              {auditPlan.achieved_date && (
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 bg-emerald-100 rounded-full flex items-center justify-center">
                      <Award className="h-4 w-4 text-emerald-600" />
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-xs font-medium text-gray-700">Archivé</p>
                    <p className="text-sm text-gray-900">{new Date(auditPlan.achieved_date).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                  </div>
                </div>
              )}
              {auditPlan.publish_date && (
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 bg-indigo-100 rounded-full flex items-center justify-center">
                      <Eye className="h-4 w-4 text-indigo-600" />
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-xs font-medium text-gray-700">Publié</p>
                    <p className="text-sm text-gray-900">{new Date(auditPlan.publish_date).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                  </div>
                </div>
              )}
              {auditPlan.publication_paused_date && (
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 bg-orange-100 rounded-full flex items-center justify-center">
                      <Pause className="h-4 w-4 text-orange-600" />
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-xs font-medium text-gray-700">Publication suspendue</p>
                    <p className="text-sm text-gray-900">{new Date(auditPlan.publication_paused_date).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Location Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <MapPin className="h-5 w-5 mr-2 text-green-600" />
              Localisation
            </h2>
            <div className="flex items-center p-3 bg-gray-50 rounded-lg border border-gray-200">
              <MapPin className="h-5 w-5 text-gray-600 mr-2" />
              <p className="text-sm text-gray-900">{auditPlan.location}</p>
            </div>
          </div>

          {/* Attachments Card - Always show for debugging */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <FileText className="h-5 w-5 mr-2 text-green-600" />
              Pièces jointes ({auditPlan.attachments ? auditPlan.attachments.length : 0})
            </h2>
            <div className="space-y-3">
              {auditPlan.attachments && auditPlan.attachments.length > 0 ? (
                auditPlan.attachments.map((attachment) => (
                  <div key={attachment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center space-x-3">
                      <FileText className="h-5 w-5 text-green-600" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{attachment.file_name}</p>
                        <p className="text-xs text-gray-500">
                          {attachment.file_size && `${(attachment.file_size / 1024).toFixed(2)} KB • `}
                          Téléchargé le {new Date(attachment.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <button 
                      onClick={() => handleDownloadAttachment(attachment.id, attachment.file_name)}
                      className="inline-flex items-center px-3 py-1 text-sm text-green-600 hover:text-green-800 border border-green-300 rounded hover:bg-green-50 transition-colors"
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Télécharger
                    </button>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm">Aucune pièce jointe</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Debug: attachments = {JSON.stringify(auditPlan.attachments)}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Metadata Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Clock className="h-5 w-5 mr-2 text-green-600" />
              Métadonnées
            </h2>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Créé par</label>
                <p className="text-sm text-gray-900">{auditPlan.created_by}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Créé le</label>
                <p className="text-sm text-gray-900">
                  {new Date(auditPlan.created_at).toLocaleDateString('fr-FR', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Dernière mise à jour par</label>
                <p className="text-sm text-gray-900">{auditPlan.last_updated_by}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Dernière mise à jour le</label>
                <p className="text-sm text-gray-900">
                  {new Date(auditPlan.updated_at).toLocaleDateString('fr-FR', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
